import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../components/SEO';
import PageShell from '../components/site/PageShell';
import Lightbox from '../components/site/Lightbox';
import PlatformDiagram from '../components/site/PlatformDiagram';
import { images } from '../config/esanzaContent';
import { platformModules, positioning, ctas, PlatformModule } from '../config/product';
import { useWebsiteContent } from '../hooks/useWebsiteContent';

const screenshotImages = [
  { src: '/images/esanza/platform-gallery/dashboard-01.png', label: 'Farm Dashboard' },
  { src: '/images/esanza/platform-gallery/dashboard-02.png', label: 'Field & Crop Records' },
  { src: '/images/esanza/platform-gallery/dashboard-03.png', label: 'Field Overview' },
  { src: '/images/esanza/platform-gallery/dashboard-04.png', label: 'Training Hub' },
  { src: '/images/esanza/platform-gallery/dashboard-05.png', label: 'Analytics View' },
];

const STATUS_TONE: Record<string, string> = {
  Live: 'border-emerald-400/40 bg-emerald-400/10 text-emerald-300',
  Pilot: 'border-sky-400/40 bg-sky-400/10 text-sky-300',
  Beta: 'border-[#d4af37]/50 bg-[#d4af37]/15 text-[#f3d978]',
  Planned: 'border-white/25 bg-white/10 text-white/60',
};

const ModuleCard: React.FC<{ module: PlatformModule }> = ({ module }) => (
  <article className="grid gap-5 rounded-2xl border border-[#dbe5d2] bg-white p-7 shadow-sm transition hover:-translate-y-1 hover:shadow-xl lg:grid-cols-[1fr_1.15fr]">
    <div>
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#14532d] text-sm font-black text-[#d4af37]">
          {module.number}
        </span>
        {module.status && (
          <span
            className={`rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.14em] ${
              STATUS_TONE[module.status] || STATUS_TONE.Planned
            }`}
          >
            {module.status}
          </span>
        )}
      </div>
      <h3 className="mt-4 text-2xl font-black text-[#102016]">{module.title}</h3>
      <p className="mt-2 text-sm font-semibold text-[#14532d]">{module.summary}</p>
      <p className="mt-3 text-sm leading-6 text-[#596959]">{module.does}</p>
    </div>
    <dl className="grid gap-3 self-start rounded-xl border border-[#eef2e8] bg-[#f7f9f4] p-5">
      <div>
        <dt className="text-[11px] font-black uppercase tracking-[0.16em] text-[#987a1f]">Who uses it</dt>
        <dd className="mt-1 text-sm text-[#3d4a3d]">{module.users}</dd>
      </div>
      <div>
        <dt className="text-[11px] font-black uppercase tracking-[0.16em] text-[#987a1f]">How it connects</dt>
        <dd className="mt-1 text-sm text-[#3d4a3d]">{module.connects}</dd>
      </div>
    </dl>
  </article>
);

const PlatformPage: React.FC = () => {
  const { t } = useTranslation();
  const { page } = useWebsiteContent('platform');
  const workflow = t('site.platform.workflow', { returnObjects: true }) as Array<[string, string, string]>;
  const operatingLayers = t('site.platform.layers', { returnObjects: true }) as Array<[string, string]>;
  const cleanContent = (value: string, fallback: string) => {
    const blocked = /pmvp|funding-ready|secure setup link|see traceability/i;
    return blocked.test(value || '') ? fallback : value;
  };
  const fallbackTitle = 'The operating system for agriculture.';
  const fallbackSub =
    'Farm management, field intelligence, traceability, supply-chain operations and B2B market access — one connected system.';
  const headline = cleanContent(page?.title || t('site.platform.title', fallbackTitle), t('site.platform.title', fallbackTitle));
  const subtext = cleanContent(
    page?.summary || t('site.platform.subtitle', fallbackSub),
    t('site.platform.subtitle', fallbackSub)
  );
  const modules: PlatformModule[] = platformModules;

  /* ── lightbox state ────────────────────────────────── */
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const openLightbox = useCallback((i: number) => setActiveIdx(i), []);
  const closeLightbox = useCallback(() => setActiveIdx(null), []);
  const prevImage = useCallback(() => setActiveIdx((i) => (i !== null ? (i - 1 + screenshotImages.length) % screenshotImages.length : null)), []);
  const nextImage = useCallback(() => setActiveIdx((i) => (i !== null ? (i + 1) % screenshotImages.length : null)), []);
  const activeImage = activeIdx !== null ? screenshotImages[activeIdx] : null;

  return (
    <>
      <SEO
        title="Platform | eSanza"
        description="The eSanza agricultural operating system: farm management, field operations, AI field intelligence, traceability, training, inputs, warehouse, marketplace, logistics and analytics."
        imagePath={images.app}
        keywords="farm management software, agricultural operating system, traceability software, agricultural supply chain, farm analytics, agricultural field intelligence"
        url="https://esanza.com/platform"
      />
      <PageShell>
        {/* ── Hero ─────────────────────────────────────── */}
        <section className="relative overflow-hidden px-5 pb-16 pt-32 lg:px-8">
          <div className="absolute inset-0">
            <img src={images.app} alt="" className="h-full w-full object-cover opacity-20" />
            <div className="absolute inset-0 bg-[linear-gradient(90deg,#06150e_0%,rgba(6,21,14,.96)_45%,rgba(6,21,14,.62)_100%)]" />
          </div>

          <div className="relative mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-end">
            <div>
              <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-[#d4af37]">
                {t('site.platform.label', 'eSanza platform')}
              </p>
              <h1 className="mt-5 max-w-4xl text-5xl font-black leading-[1] tracking-tight text-white md:text-6xl">
                {headline}
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-white/70">{subtext}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to={ctas.secondary.to} className="rounded-full bg-[#d4af37] px-7 py-4 text-sm font-extrabold text-[#06150e]">
                  {ctas.secondary.label}
                </Link>
                <Link to="/contact" className="rounded-full border border-white/18 px-7 py-4 text-sm font-extrabold text-white hover:bg-white/10">
                  Talk to eSanza
                </Link>
                <Link to="/field-intelligence" className="rounded-full px-7 py-4 text-sm font-extrabold text-[#f3d978] underline-offset-4 hover:underline">
                  Field Intelligence
                </Link>
              </div>
            </div>

            {/* clickable hero screenshot */}
            <motion.button
              onClick={() => openLightbox(0)}
              whileHover={{ scale: 1.015 }}
              whileTap={{ scale: 0.985 }}
              className="group grid gap-3 rounded-lg border border-white/12 bg-white/[0.06] p-4 shadow-2xl shadow-black/30 backdrop-blur text-left cursor-pointer transition-shadow hover:shadow-[#d4af37]/10"
            >
              <div className="relative overflow-hidden rounded-lg">
                <img
                  src={images.app}
                  alt="eSanza dashboard and mobile platform — click to enlarge"
                  className="max-h-[620px] w-full rounded-lg object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
                />
                <div className="absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full border border-white/20 bg-[#06150e]/80 px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-widest text-white/80 opacity-0 transition-opacity duration-300 group-hover:opacity-100 backdrop-blur">
                  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                    <circle cx="11" cy="11" r="7" />
                    <path d="M21 21l-4.35-4.35" />
                  </svg>
                  Zoom
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                {operatingLayers.map(([title, text]) => (
                  <div key={title} className="rounded-lg border border-white/10 bg-black/18 p-4">
                    <h2 className="text-sm font-black text-white">{title}</h2>
                    <p className="mt-2 text-xs leading-5 text-white/58">{text}</p>
                  </div>
                ))}
              </div>
            </motion.button>
          </div>
        </section>

        {/* ── Architecture diagram ─────────────────────── */}
        <section className="bg-[#0a1f14] px-5 py-20 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="mx-auto max-w-3xl text-center">
              <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-[#d4af37]">Architecture</p>
              <h2 className="mt-4 text-4xl font-black tracking-tight text-white md:text-5xl">
                One system, not a stack of tools.
              </h2>
              <p className="mt-5 text-base leading-7 text-white/60">
                {positioning.short} Farm operations, intelligence and market activity share one traceability spine and one
                analytics layer.
              </p>
            </div>
            <PlatformDiagram className="mt-10" />
          </div>
        </section>

        {/* ── Modules ──────────────────────────────────── */}
        <section className="bg-[#f7f9f4] px-5 py-20 text-[#102016] lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#987a1f]">
                {t('site.platform.modules_label', 'Modules')}
              </p>
              <h2 className="mt-4 text-4xl font-black tracking-tight md:text-5xl">
                {t('site.platform.modules_title', 'Ten modules. One shared workflow.')}
              </h2>
              <p className="mt-5 text-base leading-7 text-[#596959]">
                Each module says what it does, who uses it, and how it hands data to the next step — because a module that
                cannot connect is a module that creates re-entry work.
              </p>
            </div>

            <div className="mt-10 grid gap-5">
              {modules.map((module) => (
                <ModuleCard key={module.id} module={module} />
              ))}
            </div>
          </div>
        </section>

        {/* ── Screenshot Gallery ────────────────────────── */}
        <section className="bg-white px-5 py-20 text-[#102016] lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#987a1f]">
                {t('site.platform.gallery_label', 'Platform screenshots')}
              </p>
              <h2 className="mt-4 text-4xl font-black tracking-tight md:text-5xl">
                {t('site.platform.gallery_title', 'Take a closer look.')}
              </h2>
              <p className="mt-5 text-base leading-7 text-[#596959]">
                {t('site.platform.gallery_text', 'Click any screenshot to view it in full size.')}
              </p>
            </div>

            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {screenshotImages.map((img, i) => (
                <motion.button
                  key={img.src}
                  onClick={() => openLightbox(i)}
                  initial={{ opacity: 0, y: 36, scale: 0.96 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ duration: 0.5, delay: i * 0.07 }}
                  whileHover={{ y: -6, scale: 1.02 }}
                  className="group relative overflow-hidden rounded-xl border border-[#dbe5d2] bg-[#f7f9f4] shadow-md shadow-[#102016]/[0.05] transition-shadow hover:shadow-xl hover:shadow-[#d4af37]/12 text-left cursor-pointer"
                >
                  <div className="overflow-hidden">
                    <img
                      src={img.src}
                      alt={img.label}
                      loading="lazy"
                      className="h-52 w-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  </div>

                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-[#06150e]/0 transition-colors duration-500 group-hover:bg-[#06150e]/30">
                    <div className="flex h-11 w-11 items-center justify-center rounded-full border border-white/25 bg-white/15 text-white opacity-0 transition-all duration-400 group-hover:opacity-100 group-hover:scale-100 scale-75 backdrop-blur">
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                        <circle cx="11" cy="11" r="7" />
                        <path d="M21 21l-4.35-4.35" />
                      </svg>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 px-5 py-4">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#14532d] text-[10px] font-black text-[#d4af37]">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="text-sm font-extrabold tracking-tight text-[#102016]">{img.label}</span>
                  </div>
                </motion.button>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-12 text-center"
            >
              <Link
                to="/gallery"
                className="inline-flex items-center gap-2 rounded-full border border-[#dbe5d2] px-7 py-3.5 text-sm font-extrabold text-[#102016] transition hover:bg-[#102016] hover:text-white"
              >
                View full gallery
                <span>→</span>
              </Link>
            </motion.div>
          </div>
        </section>

        {/* ── Workflow ─────────────────────────────────── */}
        <section className="bg-[#f7f9f4] px-5 py-20 text-[#102016] lg:px-8">
          <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div>
              <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#987a1f]">
                {t('site.platform.workflow_label', 'Operating workflow')}
              </p>
              <h2 className="mt-4 text-4xl font-black tracking-tight md:text-5xl">
                {t('site.platform.workflow_title', 'Data in once. Used everywhere.')}
              </h2>
              <p className="mt-5 text-base leading-8 text-[#596959]">
                {t(
                  'site.platform.workflow_text',
                  'Field records feed intelligence, intelligence proposes work, completed work becomes evidence, and evidence becomes supply. Nothing is entered only for reporting.'
                )}
              </p>
            </div>
            <div className="grid gap-3">
              {workflow.map(([step, title, text]) => (
                <div key={step} className="grid gap-4 rounded-lg border border-[#dbe5d2] bg-white p-5 sm:grid-cols-[70px_1fr]">
                  <div className="text-3xl font-black text-[#14532d]">{step}</div>
                  <div>
                    <h3 className="text-lg font-black">{title}</h3>
                    <p className="mt-2 text-sm leading-6 text-[#596959]">{text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA banner ──────────────────────────────── */}
        <section className="relative overflow-hidden px-5 py-20 lg:px-8">
          <img src={images.traceabilityPhoto || images.traceability} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-24" />
          <div className="absolute inset-0 bg-[#06150e]/88" />
          <div className="relative mx-auto grid max-w-7xl gap-8 md:grid-cols-[1fr_auto] md:items-center">
            <div>
              <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-[#d4af37]">
                {t('site.platform.pilot_label', 'Deployment status')}
              </p>
              <h2 className="mt-4 max-w-3xl text-4xl font-black tracking-tight text-white md:text-5xl">
                {t('site.platform.pilot_title', 'A working platform. Field validation underway.')}
              </h2>
              <p className="mt-5 max-w-2xl text-base leading-8 text-white/68">
                {t(
                  'site.platform.pilot_text',
                  'Farm mapping, field teams, traceability records and controlled marketplace activation already run in one organisation workspace.'
                )}
              </p>
            </div>
            <Link to={ctas.secondary.to} className="rounded-full bg-[#d4af37] px-7 py-4 text-sm font-extrabold text-[#06150e]">
              {ctas.secondary.label}
            </Link>
          </div>
        </section>
      </PageShell>

      {/* ── Lightbox ───────────────────────────────────── */}
      <AnimatePresence>
        {activeImage && (
          <Lightbox
            image={activeImage}
            index={activeIdx!}
            total={screenshotImages.length}
            images={screenshotImages}
            onPrev={prevImage}
            onNext={nextImage}
            onClose={closeLightbox}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default PlatformPage;
